import get from 'lodash/get';
import camelCase from 'lodash/camelCase';

const getCommonProperties = verticalProperties => ({
  ecoScore: verticalProperties.ecoScore,
  images: verticalProperties.images || get(verticalProperties, ['image_info', 'images']),
  totalImages: verticalProperties.totalImages || get(verticalProperties, ['image_info', 'total']),
  price: {
    value: get(verticalProperties, ['price', 'value']),
    valueFormatted:
      get(verticalProperties, ['price', 'valueFormatted']) ||
      get(verticalProperties, ['price', 'value_formatted']),
    oldValue:
      get(verticalProperties, ['price', 'oldValue']) ||
      get(verticalProperties, ['price', 'old_value']),
    oldValueFormatted:
      get(verticalProperties, ['price', 'oldValueFormatted']) ||
      get(verticalProperties, ['price', 'old_value_formatted']),
    isWeekly:
      get(verticalProperties, ['price', 'isWeekly']) ||
      get(verticalProperties, ['price', 'is_weekly']),
  },
});

const getHomesProperties = verticalProperties => ({
  ...getCommonProperties(verticalProperties),
  propertyType: verticalProperties.propertyType,
  year: verticalProperties.year,
  rooms: verticalProperties.rooms,
  bathrooms: verticalProperties.bathrooms,
  plotArea: verticalProperties.plotArea,
  orientation: verticalProperties.orientation,
  furnished: verticalProperties.furnished,
  rentToOwn: verticalProperties.rentToOwn,
  floorNumber: verticalProperties.floorNumber,
  overseas: verticalProperties.overseas,
  floorArea: verticalProperties.floorArea || verticalProperties.floor_area,
  parking: verticalProperties.parking,
  priceMeter: verticalProperties.priceMeter,
  newBuilding: verticalProperties.newBuilding,
  foreclosure: verticalProperties.foreclosure,
  condition: verticalProperties.condition,
  byOwner: verticalProperties.byOwner,
});

const getCarsProperties = verticalProperties => ({
  ...getCommonProperties(verticalProperties),
  make: verticalProperties.make,
  model: verticalProperties.model,
  mileage: verticalProperties.mileage,
  year: verticalProperties.year,
  doors: verticalProperties.doors,
  type: verticalProperties.type,
  power: verticalProperties.power,
  fuel: verticalProperties.fuel,
  transmission: verticalProperties.transmission,
  color: verticalProperties.color,
  sizeEngine: verticalProperties.sizeEngine,
  seats: verticalProperties.seats,
  gears: verticalProperties.gears,
  guarantee: verticalProperties.guarantee,
  co2Emissions: verticalProperties.co2Emissions,
  fuelConsumption: verticalProperties.fuelConsumption,
  bestValue: verticalProperties.bestValue,
});

const getJobsProperties = verticalProperties => ({
  salary: get(verticalProperties, ['salary', 'valueFormatted']) || verticalProperties.salary_text,
  category: verticalProperties.category || verticalProperties.categoryId,
  telecommute: verticalProperties.telecommute,
  jobType: verticalProperties.jobType || verticalProperties.contract,
  workingHours: verticalProperties.workingHours || verticalProperties.working_hours,
  experience: verticalProperties.experience,
  company: verticalProperties.company,
  requirements: verticalProperties.requirements,
  vacancies: verticalProperties.vacancies,
});

const getProductsProperties = verticalProperties => ({
  ...getCommonProperties(verticalProperties),
  categoryId: verticalProperties.categoryId || verticalProperties.category_id,
});

const getVerticalProperties = (verticalName, verticalProperties) => {
  let properties = null;

  switch (verticalName) {
    case 'homes':
      properties = getHomesProperties(verticalProperties);
      break;

    case 'cars':
      properties = getCarsProperties(verticalProperties);
      break;

    case 'jobs':
      properties = getJobsProperties(verticalProperties);
      break;

    case 'products':
      properties = getProductsProperties(verticalProperties);
      break;

    default:
      break;
  }

  return properties;
};

function normalizeAd(data, verticalName, visitCount) {
  const verticalProperties =
    data.verticalProperties || get(data, ['attributes', 'vertical_properties']) || [];
  const source = data.source || get(data, ['attributes', 'source']);
  const adDescription = data.description || get(data, ['attributes', 'description']);

  return {
    id: data.id,
    date: data.date || get(data, ['attributes', 'date']),
    timeAgoSeconds: data.timeAgoSeconds,
    title: data.title || get(data, ['attributes', 'title']),
    description: adDescription,
    descriptionOriginal:
      data.descriptionOriginal || get(data, ['attributes', 'description_original'], null),
    url: data.url || get(data, ['attributes', 'url']),
    redirectUrl: data.redirectUrl,
    premium: data.premium || get(data, ['attributes', 'is_premium']),
    fresh: data.fresh || get(data, ['attributes', 'is_fresh']),
    section_id: data.section_id || get(data, ['attributes', 'section_id']),
    section_type_id: data.section_type_id || get(data, ['attributes', 'section_type_id']),
    isAlternativeAd: data.isAlternativeAd,
    isSimilarContentAd: data.isSimilarContentAd || false,
    position: data.position || get(data, ['attributes', 'position']) || 0,
    verticalProperties: {
      ...getVerticalProperties(verticalName, verticalProperties),
      typeId: verticalProperties.typeId || verticalProperties.type_id || 1,
      address: verticalProperties.address,
      amenities: verticalProperties.amenities
        ? verticalProperties.amenities.map(amenity => camelCase(amenity))
        : [],
      geoOsm: verticalProperties.geoInfo,
    },

    location: {
      latitude:
        get(data, ['location', 'latitude']) || get(data, ['attributes', 'location', 'latitude']),
      longitude:
        get(data, ['location', 'longitude']) || get(data, ['attributes', 'location', 'longitude']),
      cityInfo:
        get(data, ['location', 'cityInfo']) || get(data, ['attributes', 'location', 'city_info']),
      city: get(data, ['location', 'city']) || get(data, ['attributes', 'location', 'city']),
      cityArea:
        get(data, ['location', 'cityArea']) || get(data, ['attributes', 'location', 'city_area']),
      region: get(data, ['location', 'region']) || get(data, ['attributes', 'location', 'region']),
      postalCode: data.postCode,
    },

    source: {
      id: get(source, 'id'),
      name: get(source, 'name'),
      ad: {
        id: get(source, ['ad', 'id']),
        url: get(source, ['ad', 'url']),
        hasAdpageForm:
          get(source, ['ad', 'hasAdpageForm']) || get(source, ['ad', 'has_adpage_form']) || false,
        contactEmail: get(source, ['ad', 'contactEmail']) || get(source, ['ad', 'contact_email']),
        contactPhone: get(source, ['ad', 'contactPhone']) || get(source, ['ad', 'contact_phone']),
        campaignIds:
          get(source, ['ad', 'campaignIds']) || get(source, ['ad', 'sales', 'campaign_ids']),
        cpcMobile:
          get(source, ['ad', 'cpcMobile']) || get(source, ['ad', 'sales', 'cpc', 'mobile']),
        cpcDesktop:
          get(source, ['ad', 'cpcDesktop']) || get(source, ['ad', 'sales', 'cpc', 'desktop']),
      },
    },
    visitCount,
    distanceFromWhere: data.distanceFromWhere || null,
    totalSources: data.totalSources,
  };
}

export default normalizeAd;



// WEBPACK FOOTER //
// ./app/normalize/ad.js